import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Drawer } from "flowbite-react";
import { Bars3Icon } from "@heroicons/react/16/solid";
import {
  HomeIcon,
  UserGroupIcon,
  CubeIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ChevronDownIcon,
  SparklesIcon,
  CheckBadgeIcon,
  GiftIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";

import { logout } from "../../../../Redux Store/Slices/auth";
import AtmanirbharFinsLogo from "../../../../assets/AtmanirbharFinsLogo2026.jpg";
import dummyProfile from "../../../../assets/dummy-profile.png";
import { useDispatch, useSelector } from "react-redux";

const menuList = [
  { title: "Home", link: "/", icon: HomeIcon, emoji: "🏠" },
  { title: "Leads", link: "/leads", icon: UserGroupIcon, emoji: "🌟" },
  { title: "Products", link: "/products", icon: CubeIcon, emoji: "💰" },
  { title: "Learn", link: "/courses", icon: AcademicCapIcon, emoji: "🎓" },
  { title: "Invoice", link: "/invoices", icon: DocumentTextIcon, emoji: "🧾" },
  { title: "Advisor Pro", link: "/pro", icon: SparklesIcon, emoji: "👑" },
  { title: "Certification", link: "/certification", icon: CheckBadgeIcon, emoji: "🎖️" },
  { title: "Refer & Earn", link: "/referral", icon: GiftIcon, emoji: "🎁" },
  { title: "Support", link: "/support", icon: ChatBubbleLeftRightIcon, emoji: "💬" },
];

const extraDrawerItems = [
  { title: "Certificates", link: "/certificates", emoji: "📄" },
  { title: "Help Center", link: "/help", emoji: "❓" },
  { title: "Terms & Conditions", link: "/terms", emoji: "📑" },
];

function getStatusBadge(status) {
  if (!status) return null;
  const map = {
    Approved: { bg: "#D1FADF", text: "#027A48", label: "✓ Verified" },
    Pending: { bg: "#FEF0C7", text: "#B54708", label: "⏳ Pending" },
    "Re-Submitted": { bg: "#FEF0C7", text: "#B54708", label: "⏳ Pending" },
    Rejected: { bg: "#FEE4E2", text: "#B42318", label: "✗ Rejected" },
  };
  return map[status] || null;
}

const Appbar = () => {
  const loggedInUser = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setProfileOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    setProfileOpen(false);
    dispatch(logout());
    navigate("/login");
  };

  const onboardingStatus = loggedInUser?.user?.advisor_onboarding_status_id?.title;
  const badge = getStatusBadge(onboardingStatus);

  return (
    <>
      <div className="flex py-1 px-4 border-b border-b-[#E2E8F0] bg-white sticky top-0 z-40">
        <div className="w-full flex flex-row items-center justify-between gap-6">

          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <img
              className="h-20 w-auto max-w-[320px] object-contain"
              src={AtmanirbharFinsLogo}
              alt="AtmanirbharFins"
            />
          </div>

          {/* Hamburger — mobile only */}
          <div className="flex items-center max-[700px]:flex hidden ml-auto">
            <button
              onClick={() => setIsOpen(true)}
              className="p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors"
            >
              <Bars3Icon className="w-6 h-6 text-[#64748B]" />
            </button>
          </div>

          {/* Desktop nav */}
          <nav className="flex items-center gap-1 max-[700px]:hidden">
            {menuList.map((item, index) => {
              const Icon = item.icon;
              return (
                <NavLink
                  to={item.link}
                  key={`nav-${index}`}
                  className={({ isActive }) =>
                    [
                      "flex items-center gap-1.5 px-3 py-2 rounded-lg text-[0.875rem] font-medium transition-colors",
                      isActive
                        ? "text-[#0184FF] bg-[#EAF4FF]"
                        : "text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC]",
                    ].join(" ")
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className={`w-4 h-4 ${isActive ? "text-[#0184FF]" : "text-[#94A3B8]"}`} />
                      {item.title}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Desktop profile dropdown */}
          <div className="relative max-[700px]:hidden" ref={profileRef}>
            <button
              onClick={() => setProfileOpen((p) => !p)}
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC] transition-colors"
            >
              <img src={dummyProfile} className="w-8 h-8 rounded-lg object-cover" alt="profile" />
              <div className="text-left">
                <p className="text-[0.8125rem] font-semibold text-[#0F172A] leading-tight">
                  {loggedInUser?.user?.name || "Advisor"}
                </p>
                <div className="flex items-center gap-1.5">
                  {loggedInUser?.user?.advisor_display_id && (
                    <p className="text-[0.6875rem] text-[#64748B] leading-tight">
                      {loggedInUser.user.advisor_display_id}
                    </p>
                  )}
                  {badge && (
                    <span
                      className="text-[0.625rem] font-semibold px-1.5 py-0.5 rounded-md leading-tight"
                      style={{ backgroundColor: badge.bg, color: badge.text }}
                    >
                      {badge.label}
                    </span>
                  )}
                </div>
              </div>
              <ChevronDownIcon className={`w-4 h-4 text-[#94A3B8] transition-transform ${profileOpen ? "rotate-180" : ""}`} />
            </button>

            {profileOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-[#E2E8F0] rounded-xl shadow-lg overflow-hidden z-50">
                <button
                  onClick={() => { setProfileOpen(false); navigate("/profile"); }}
                  className="flex items-center gap-2.5 w-full px-4 py-3 text-[0.875rem] text-[#0F172A] hover:bg-[#F8FAFC] transition-colors"
                >
                  <UserCircleIcon className="w-4 h-4 text-[#64748B]" />
                  Profile
                </button>
                <div className="border-t border-[#E2E8F0]" />
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2.5 w-full px-4 py-3 text-[0.875rem] text-red-500 hover:bg-red-50 transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  Logout
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      <SideDrawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        handleLogout={handleLogout}
        loggedInUser={loggedInUser}
        badge={badge}
        onboardingStatus={onboardingStatus}
      />
    </>
  );
};

export default Appbar;

function SideDrawer({ isOpen, setIsOpen, handleLogout, loggedInUser, badge, onboardingStatus }) {
  const navigate = useNavigate();

  return (
    <Drawer
      open={isOpen}
      onClose={() => setIsOpen(false)}
      className="w-3/6 max-sm:w-[80%] p-0 flex flex-col"
      style={{ backgroundColor: "#F8FEFF" }}
    >
      {/* Close button */}
      <div className="flex justify-end px-3 pt-3">
        <button
          onClick={() => setIsOpen(false)}
          className="p-1.5 rounded-lg hover:bg-white transition-colors"
        >
          <XMarkIcon className="w-5 h-5 text-[#64748B]" />
        </button>
      </div>

      <div className="flex flex-col flex-1 px-3 pb-4 gap-3 overflow-y-auto">

        {/* User card — mirrors mobile app's UserCard */}
        <button
          onClick={() => { setIsOpen(false); navigate("/profile"); }}
          className="w-full text-left bg-white rounded-xl p-4 flex flex-row items-start gap-3"
          style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.12)" }}
        >
          <img
            src={dummyProfile}
            alt="avatar"
            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
          />
          <div className="min-w-0">
            <p className="font-bold text-[1rem] text-[#1C2B36] truncate">
              {loggedInUser?.user?.name || "Advisor"}
            </p>
            <p className="text-[0.875rem] text-[#667085] truncate mb-2">
              {loggedInUser?.user?.email || loggedInUser?.user?.advisor_display_id || ""}
            </p>
            {badge && (
              <span
                className="text-[0.75rem] font-semibold px-2 py-0.5 rounded-lg"
                style={{ backgroundColor: badge.bg, color: badge.text }}
              >
                {badge.label}
              </span>
            )}
          </div>
        </button>

        {/* Rejected remark card */}
        {onboardingStatus === "Rejected" && (
          <div className="bg-[#FFF4F4] border border-[#F04438] rounded-xl p-4">
            <p className="font-bold text-[#B42318] mb-1">❌ Rejected Application</p>
            <p className="text-[0.875rem] text-[#B42318] mb-3">
              {loggedInUser?.user?.rejection_remark?.slice(-1)?.[0]?.reason ||
                "Please fill again with proper attachments."}
            </p>
            <button
              onClick={() => { setIsOpen(false); navigate("/advisor-onboarding"); }}
              className="w-full bg-[#F04438] text-white text-[0.875rem] font-semibold py-2.5 rounded-lg"
            >
              Resubmit
            </button>
          </div>
        )}

        {/* Main nav items */}
        {menuList.map((item, index) => (
          <NavLink
            to={item.link}
            key={`drawer-${index}`}
            onClick={() => setIsOpen(false)}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 px-4 py-3.5 rounded-xl text-[1rem] font-semibold transition-colors",
                isActive
                  ? "bg-[#EAF4FF] text-[#0184FF] border-l-4 border-[#0184FF]"
                  : "bg-white text-[#1C2B36]",
              ].join(" ")
            }
            style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.10)" }}
          >
            <span className="text-[1.125rem]">{item.emoji}</span>
            {item.title}
          </NavLink>
        ))}

        {/* Divider */}
        <div className="border-t border-[#E2E8F0] my-1" />

        {/* Extra items */}
        {extraDrawerItems.map((item, index) => (
          <NavLink
            to={item.link}
            key={`extra-${index}`}
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-white text-[1rem] font-semibold text-[#1C2B36] transition-colors hover:bg-[#F0F9FF]"
            style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.10)" }}
          >
            <span className="text-[1.125rem]">{item.emoji}</span>
            {item.title}
          </NavLink>
        ))}

        {/* Logout */}
        <button
          onClick={() => { setIsOpen(false); handleLogout(); }}
          className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-white text-[1rem] font-semibold text-[#1C2B36] hover:bg-red-50 hover:text-red-600 transition-colors w-full text-left"
          style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.10)" }}
        >
          <span className="text-[1.125rem]">🚪</span>
          Log Out
        </button>

        {/* Version — mirrors mobile app */}
        <div className="flex justify-center pt-2">
          <p className="text-[0.75rem] text-[#999999] font-medium">Web v1.0</p>
        </div>

      </div>
    </Drawer>
  );
}
